import React, { MouseEvent } from 'react';
import IForm from '../../../../types/IForm';
import { MoveContentBlockForm } from '../forms/MoveContentBlockForm/MoveContentBlockForm';
import { actionKeyToIcon, getIndex, isActionDisabled } from '../../../blocks/ContentWrapper/ContentWrapper.utils';
import { Namespace } from '../../../../translation/namespace';
import Btn, { BtnModifier } from '../../../molecules/Btn';
import { TooltipPlacement } from '../../../atoms/Tooltip';
import { BlockType } from '../../../../types/api/ElementType';
import { ActionState, ErrorState } from '../../../blocks/ContentWrapper/useContentWrapper';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { sendLiveRegionMessage } from '../../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { AddContentBlockForm } from '../forms/AddContentBlockForm/AddContentBlockForm';
import {
  ContentWrapperAction as Action,
  ContentWrapperBlocks,
  ContentWrapperBlockTypes,
} from '../../../blocks/ContentWrapper/ContentWrapper.types';
import { ToggleActiveView } from '../BlockInteractionsTypes';

type DeleteBlockAction = {
  actionType: Action.deleteBlock;
  elementType: Exclude<ContentWrapperBlockTypes, BlockType.layoutgroup>;
};

type ConnectBlocksAction = {
  actionType: Action.connectBlocks;
  elementType: BlockType.contentimage | BlockType.layoutgroup | BlockType.externalcontent | BlockType.pdffiledetails;
};

type MoveBlockAction = {
  actionType: Action.moveBlockUp | Action.moveBlockDown;
  elementType: ContentWrapperBlockTypes;
};

type AddBlockAction = {
  actionType: Action.addBlock;
  elementType: ContentWrapperBlockTypes;
};

type BlockAction = DeleteBlockAction | ConnectBlocksAction | MoveBlockAction | AddBlockAction;

type InteractionButtonProps = BlockAction & {
  /** UUID of rendered Block */
  uuid: string;
  form: IForm;
  toggleActiveView: ToggleActiveView;
  isNestedInCollapsibleOrLayoutGroup: boolean;

  /** Blocks' index inside the ContentWrapper */
  index: number;
  /** The Connect Action shall only be enabled on a contentimage block followed by a richtext block */
  nextBlockIsRichtext: boolean;
  /** Number of Blocks the ContentWrapper handles */
  numBlocks: number;
  onMovingFailure: (uuid: string) => void;
  onMovingSuccess: (actionType: string, uuid: string, elementType: string) => void;
  reference?: React.RefObject<HTMLButtonElement>;
  setAction: (action: ActionState) => void;
  addBlock: (newBlock: ContentWrapperBlocks, position: number) => void;
  setError: React.Dispatch<React.SetStateAction<ErrorState | null>>;
  parentIndex: number | undefined;
  belowIsCollapsibleContainer: number | undefined,
  aboveIsCollapsibleContainer: number | undefined,
};

/** Renders ContentWrapper actions */
export function InteractionButton({
  actionType,
  elementType,
  form,
  index,
  isNestedInCollapsibleOrLayoutGroup,
  nextBlockIsRichtext,
  numBlocks,
  onMovingFailure,
  onMovingSuccess,
  reference,
  setAction,
  addBlock,
  setError,
  toggleActiveView,
  uuid,
  parentIndex,
  belowIsCollapsibleContainer,
  aboveIsCollapsibleContainer,
}: InteractionButtonProps) {
  const { t } = useSonTranslation(Namespace.ContentWrapper);

  const onClick = (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    toggleActiveView(event, actionType);
  };

  const moveButtonPosition = () => {
    if (actionType === Action.moveBlockUp) {
      // first element in collapsible
      if (parentIndex !== undefined && index === 0) {
        return getIndex(parentIndex);
      }

      // element not in collapsible but above collapsible
      if (aboveIsCollapsibleContainer !== undefined) {
        return getIndex(aboveIsCollapsibleContainer, index - 1);
      }

      return getIndex(index - 1, parentIndex);
    }

    if (actionType === Action.moveBlockDown) {
      // last element in collapsible
      if (parentIndex !== undefined && numBlocks === index + 1) {
        return getIndex(parentIndex + 1);
      }

      // element not in collapsible but below collapsible
      if (belowIsCollapsibleContainer !== undefined) {
        return getIndex(belowIsCollapsibleContainer, index + 1);
      }

      return getIndex(index + 1, parentIndex);
    }

    return '';
  };

  const addButtonPosition = () => {
    return getIndex(index + 1, parentIndex);
  };

  const deleteButtonPosition = () => {
    return getIndex(index, parentIndex);
  };

  const connectButtonPosition = () => {
    if (elementType === BlockType.layoutgroup) {
      return {
        positionImage: getIndex(0, index, parentIndex),
        positionText: getIndex(1, index, parentIndex),
      };
    }

    return {
      positionImage: getIndex(index, parentIndex),
      positionText: getIndex(index + 1, parentIndex),
    };
  };

  switch (actionType) {
    case Action.moveBlockUp:
    case Action.moveBlockDown:
      return (
        <MoveContentBlockForm
          key={`contentwrapper-${actionType}`}
          elementType={elementType}
          form={form}
          actionKey={actionType}
          position={moveButtonPosition()}
          isActionDisabled={isActionDisabled(
            actionType,
            index,
            numBlocks,
            elementType,
            isNestedInCollapsibleOrLayoutGroup,
            parentIndex !== undefined,
          )}
          actionKeyToIcon={actionKeyToIcon[actionType]}
          beforeSubmit={() => {
            setAction({ uuid, type: actionType });
            sendLiveRegionMessage(t(`moveInProgress.${elementType}`));
          }}
          onSuccess={() => onMovingSuccess(actionType, uuid, elementType)}
          onFailure={() => onMovingFailure(uuid)}
        />
      );
    case Action.deleteBlock:
      return (
        <Btn
          reference={reference}
          key={`${actionType}-${uuid}`}
          data-id={actionType}
          text={t(`actions.${actionType}.text.${elementType}`, { position: deleteButtonPosition() })}
          titleText={t(
            `actions.${actionType}.titleText.${elementType}`,
            { position: deleteButtonPosition() as string },
          )}
          hiddenText
          modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
          svgSpriteId={actionKeyToIcon[actionType]}
          ariaDisabled={isActionDisabled(actionType, index, numBlocks, elementType, isNestedInCollapsibleOrLayoutGroup)}
          // aria-controls and expanded is not needed when type is modal
          routing={false}
          tooltipPlacement={TooltipPlacement.TOP}
          onClick={onClick}
        />
      );
    case Action.connectBlocks:
      return (
        <Btn
          reference={reference}
          key={`${actionType}-${uuid}`}
          data-id={actionType}
          text={t(`actions.${actionType}.text.${elementType}`, connectButtonPosition())}
          titleText={t(`actions.${actionType}.titleText.${elementType}`, connectButtonPosition())}
          hiddenText
          modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
          svgSpriteId={actionKeyToIcon[actionType]}
          ariaDisabled={
            (elementType !== BlockType.layoutgroup && nextBlockIsRichtext) ||
            isActionDisabled(actionType, index, numBlocks, elementType, isNestedInCollapsibleOrLayoutGroup)
          }
          // aria-controls and expanded is not needed when type is modal
          routing={false}
          tooltipPlacement={TooltipPlacement.TOP}
          onClick={onClick}
        />
      );
    case Action.addBlock:
      return (
        <AddContentBlockForm
          uuid={uuid}
          form={form}
          addBlock={addBlock}
          setAction={setAction}
          setError={setError}
          positionString={addButtonPosition()}
          // index starts from zero, so we have to add one
          position={index + 1}
        />
      );
    default:
      return null;
  }
}
